import { useState, useRef } from 'react';
import { locationHealthAPI } from '../lib/api';
import { toast } from 'sonner';
import {
    Upload,
    FileSpreadsheet,
    Download,
    CheckCircle,
    AlertTriangle,
    X,
    Loader2,
    HelpCircle,
    FileText
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from './ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from './ui/tooltip';
import { InfoBanner } from './UsabilityComponents';

// Help tooltip component
const HelpTooltip = ({ text }) => (
    <TooltipProvider>
        <Tooltip>
            <TooltipTrigger asChild>
                <HelpCircle className="w-3 h-3 text-gray-400 cursor-help inline-block ml-1" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs z-[100]">
                <p className="text-xs">{text}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

export default function CSVBaselineImport({ open, onOpenChange, onSuccess }) {
    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [errors, setErrors] = useState([]);
    const [step, setStep] = useState('upload'); // 'upload', 'preview', 'result'
    const [result, setResult] = useState(null);
    const fileInputRef = useRef(null);

    const resetState = () => {
        setFile(null);
        setParsedData([]);
        setErrors([]);
        setStep('upload');
        setResult(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClose = () => {
        resetState();
        onOpenChange(false);
    };

    const downloadTemplate = () => {
        const headers = [
            'location_name',
            'google_reviews',
            'google_rating',
            'facebook_reviews',
            'facebook_rating',
            'yelp_reviews',
            'yelp_rating',
            'tripadvisor_reviews',
            'tripadvisor_rating',
            'baseline_date'
        ];
        
        const exampleRow = [
            'Downtown Restaurant',
            '150',
            '4.5',
            '45',
            '4.2',
            '',
            '',
            '',
            '',
            '2025-01-15'
        ];
        
        const csvContent = [
            headers.join(','),
            exampleRow.join(','),
            '# Example: Leave empty cells for platforms you don\'t use',
            '# location_name must match exactly with your location names in YulOnTap'
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'baseline_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Template downloaded!');
    };

    const parseCSV = (text) => {
        const lines = text.split('\n').filter(line => line.trim() && !line.startsWith('#'));
        if (lines.length < 2) {
            return { data: [], errors: ['CSV file must have a header row and at least one data row'] };
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const requiredHeaders = ['location_name'];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        
        if (missingHeaders.length > 0) {
            return { data: [], errors: [`Missing required columns: ${missingHeaders.join(', ')}`] };
        }

        const data = [];
        const parseErrors = [];

        for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim());
            if (values.length !== headers.length) {
                parseErrors.push(`Row ${i + 1}: Column count mismatch (expected ${headers.length}, got ${values.length})`);
                continue;
            }

            const row = {};
            headers.forEach((header, idx) => {
                row[header] = values[idx] || '';
            });

            // Validate location_name
            if (!row.location_name) {
                parseErrors.push(`Row ${i + 1}: Missing location_name`);
                continue;
            }

            // Validate numeric fields
            const numericFields = [
                'google_reviews', 'google_rating',
                'facebook_reviews', 'facebook_rating',
                'yelp_reviews', 'yelp_rating',
                'tripadvisor_reviews', 'tripadvisor_rating'
            ];

            let hasValidData = false;
            numericFields.forEach(field => {
                if (row[field]) {
                    const num = parseFloat(row[field]);
                    if (isNaN(num)) {
                        parseErrors.push(`Row ${i + 1}: Invalid number in ${field}: "${row[field]}"`);
                    } else {
                        row[field] = num;
                        hasValidData = true;
                    }
                }
            });

            // Validate ratings are between 0 and 5
            ['google_rating', 'facebook_rating', 'yelp_rating', 'tripadvisor_rating'].forEach(field => {
                if (row[field] && (row[field] < 0 || row[field] > 5)) {
                    parseErrors.push(`Row ${i + 1}: ${field} must be between 0 and 5`);
                }
            });

            if (!hasValidData && !row.baseline_date) {
                parseErrors.push(`Row ${i + 1}: No baseline data provided for ${row.location_name}`);
                continue;
            }

            row._rowNumber = i + 1;
            row._status = 'pending';
            data.push(row);
        }

        return { data, errors: parseErrors };
    };

    const handleFileSelect = async (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        if (!selectedFile.name.endsWith('.csv')) {
            toast.error('Please select a CSV file');
            return;
        }

        setFile(selectedFile);
        setParsing(true);
        setErrors([]);

        try {
            const text = await selectedFile.text();
            const { data, errors: parseErrors } = parseCSV(text);
            
            setParsedData(data);
            setErrors(parseErrors);
            
            if (data.length > 0) {
                setStep('preview');
            } else if (parseErrors.length > 0) {
                toast.error('Failed to parse CSV. Please check the errors.');
            }
        } catch (error) {
            toast.error('Failed to read CSV file');
            setErrors(['Failed to read file: ' + error.message]);
        } finally {
            setParsing(false);
        }
    };

    const handleUpload = async () => {
        if (parsedData.length === 0) {
            toast.error('No data to upload');
            return;
        }

        setUploading(true);
        
        try {
            // Transform data for API
            const baselineData = parsedData.map(row => ({
                location_name: row.location_name,
                baseline_values: {
                    google_reviews: row.google_reviews || null,
                    google_rating: row.google_rating || null,
                    facebook_reviews: row.facebook_reviews || null,
                    facebook_rating: row.facebook_rating || null,
                    yelp_reviews: row.yelp_reviews || null,
                    yelp_rating: row.yelp_rating || null,
                    tripadvisor_reviews: row.tripadvisor_reviews || null,
                    tripadvisor_rating: row.tripadvisor_rating || null,
                    baseline_date: row.baseline_date || new Date().toISOString().split('T')[0]
                }
            }));

            const response = await locationHealthAPI.bulkSetBaselinesByName(baselineData);
            
            setResult({
                success_count: response.data.success_count || 0,
                failed_count: response.data.failed_count || 0,
                errors: response.data.errors || [],
                details: response.data.details || []
            });
            
            setStep('result');
            
            if (response.data.success_count > 0) {
                toast.success(`Successfully updated ${response.data.success_count} locations`);
                onSuccess?.();
            }
            
            if (response.data.failed_count > 0) {
                toast.warning(`${response.data.failed_count} locations failed to update`);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload baselines');
            setErrors([error.response?.data?.detail || 'Upload failed']);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="w-5 h-5 text-emerald-500" />
                        Import Baselines from CSV
                    </DialogTitle>
                    <DialogDescription>
                        Upload a CSV file to set baseline values for multiple locations at once.
                        Perfect for onboarding large clients with 100+ locations.
                    </DialogDescription>
                </DialogHeader>

                {/* Step: Upload */}
                {step === 'upload' && (
                    <div className="py-6">
                        <InfoBanner type="tip" title="How to use CSV Import">
                            1. Download the template below<br />
                            2. Fill in your location names and baseline values<br />
                            3. Upload the completed CSV file<br />
                            <strong>Note:</strong> Location names must match exactly with names in YulOnTap
                        </InfoBanner>

                        <div className="mt-6 flex justify-center">
                            <Button variant="outline" onClick={downloadTemplate}>
                                <Download className="w-4 h-4 mr-2" />
                                Download CSV Template
                            </Button>
                        </div>

                        <div className="mt-6 border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-emerald-400 transition-colors">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv"
                                onChange={handleFileSelect}
                                className="hidden"
                                id="csv-upload"
                            />
                            <label 
                                htmlFor="csv-upload"
                                className="cursor-pointer"
                            >
                                <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                                <p className="text-lg font-medium text-foreground mb-2">
                                    {parsing ? 'Parsing...' : 'Drop your CSV here or click to upload'}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    Supports .csv files up to 10MB
                                </p>
                            </label>
                            {parsing && (
                                <Loader2 className="w-6 h-6 animate-spin mx-auto mt-4 text-emerald-500" />
                            )}
                        </div>

                        {errors.length > 0 && (
                            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                                <p className="font-medium text-red-700 mb-2">Parsing Errors:</p>
                                <ul className="text-sm text-red-600 list-disc pl-4 space-y-1">
                                    {errors.slice(0, 5).map((err, idx) => (
                                        <li key={idx}>{err}</li>
                                    ))}
                                    {errors.length > 5 && (
                                        <li>...and {errors.length - 5} more errors</li>
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/* Step: Preview */}
                {step === 'preview' && (
                    <div className="flex-1 overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <Badge className="bg-emerald-100 text-emerald-700">
                                    {parsedData.length} locations found
                                </Badge>
                                {errors.length > 0 && (
                                    <Badge className="bg-amber-100 text-amber-700 ml-2">
                                        {errors.length} warnings
                                    </Badge>
                                )}
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setStep('upload')}>
                                <X className="w-4 h-4 mr-1" />
                                Change File
                            </Button>
                        </div>

                        {errors.length > 0 && (
                            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                                <p className="font-medium mb-1">Warnings (rows skipped):</p>
                                <ul className="list-disc pl-4">
                                    {errors.slice(0, 3).map((err, idx) => (
                                        <li key={idx}>{err}</li>
                                    ))}
                                    {errors.length > 3 && <li>...and {errors.length - 3} more</li>}
                                </ul>
                            </div>
                        )}

                        <div className="flex-1 overflow-auto border rounded-lg">
                            <Table>
                                <TableHeader className="sticky top-0 bg-background">
                                    <TableRow>
                                        <TableHead className="w-12">#</TableHead>
                                        <TableHead>Location Name</TableHead>
                                        <TableHead>Google</TableHead>
                                        <TableHead>Facebook</TableHead>
                                        <TableHead>Yelp</TableHead>
                                        <TableHead>Date</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {parsedData.map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell className="text-muted-foreground">{row._rowNumber}</TableCell>
                                            <TableCell className="font-medium">{row.location_name}</TableCell>
                                            <TableCell>
                                                {row.google_reviews ? (
                                                    <span>{row.google_reviews} ({row.google_rating}★)</span>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {row.facebook_reviews ? (
                                                    <span>{row.facebook_reviews} ({row.facebook_rating}★)</span>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {row.yelp_reviews ? (
                                                    <span>{row.yelp_reviews} ({row.yelp_rating}★)</span>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>{row.baseline_date || 'Today'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>

                        <DialogFooter className="mt-4">
                            <Button variant="outline" onClick={() => setStep('upload')}>
                                Back
                            </Button>
                            <Button 
                                onClick={handleUpload} 
                                disabled={uploading || parsedData.length === 0}
                                className="bg-emerald-600 hover:bg-emerald-700"
                            >
                                {uploading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 mr-2" />
                                        Import {parsedData.length} Locations
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </div>
                )}

                {/* Step: Result */}
                {step === 'result' && result && (
                    <div className="py-6">
                        <div className="text-center mb-6">
                            {result.success_count > 0 ? (
                                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
                            ) : (
                                <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-amber-500" />
                            )}
                            <h3 className="text-xl font-semibold text-foreground mb-2">
                                Import Complete
                            </h3>
                            <div className="flex justify-center gap-4">
                                <Badge className="bg-emerald-100 text-emerald-700 text-lg px-4 py-1">
                                    {result.success_count} successful
                                </Badge>
                                {result.failed_count > 0 && (
                                    <Badge className="bg-red-100 text-red-700 text-lg px-4 py-1">
                                        {result.failed_count} failed
                                    </Badge>
                                )}
                            </div>
                        </div>

                        {result.errors && result.errors.length > 0 && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                <p className="font-medium text-red-700 mb-2">Errors:</p>
                                <ul className="text-sm text-red-600 list-disc pl-4 space-y-1 max-h-40 overflow-auto">
                                    {result.errors.map((err, idx) => (
                                        <li key={idx}>{err}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <DialogFooter>
                            <Button variant="outline" onClick={resetState}>
                                Import More
                            </Button>
                            <Button onClick={handleClose}>
                                Done
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
